/**
 * ExportOptions Component - CSV, Excel, PDF export functionality
 */
import React, { useState } from 'react';
import type { SalesDataPoint, OverallMetrics } from './types';

interface ExportOptionsProps {
  data: SalesDataPoint[];
  metrics: OverallMetrics;
  periodLabel: string;
}

export const ExportOptions: React.FC<ExportOptionsProps> = ({ data, metrics, periodLabel }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  const exportCSV = () => {
    setExporting('csv');
    try {
      const headers = ['Date', 'Total Revenue', 'Total Shares', 'Bank Revenue', 'Crypto Revenue', 'Commission Shares', 'Converted Shares'];
      const rows = data.map(d => [d.label, d.revenue, d.shares, d.bankRevenue || 0, d.cryptoRevenue || 0, d.commissionShares, d.convertedShares]);
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-analytics-${periodLabel}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
      setShowMenu(false);
    }
  };

  const exportExcel = () => {
    setExporting('excel');
    try {
      // Create a more detailed CSV that Excel can open
      const headers = ['Date', 'Total Revenue', 'Total Shares', 'Bank Revenue', 'Crypto Revenue', 'Commission Shares', 'Converted Shares'];
      const summaryRows = [
        ['Summary Report', '', '', '', '', '', ''],
        ['Period', periodLabel, '', '', '', '', ''],
        ['Total Cash Revenue', `$${metrics.totalCashRevenue.toLocaleString()}`, '', '', '', '', ''],
        ['Total Cash Shares', metrics.totalCashShares.toString(), '', '', '', '', ''],
        ['Total Commission Shares', metrics.totalCommissionShares.toString(), '', '', '', '', ''],
        ['', '', '', '', '', '', ''],
        headers,
      ];
      const dataRows = data.map(d => [d.label, d.revenue, d.shares, d.bankRevenue || 0, d.cryptoRevenue || 0, d.commissionShares, d.convertedShares]);
      const csv = [...summaryRows.map(r => r.join(',')), ...dataRows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-analytics-${periodLabel}.xls`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
      setShowMenu(false);
    }
  };

  const exportPDF = async () => {
    setExporting('pdf');
    try {
      // Dynamic import jsPDF
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(18);
      doc.text('Sales Analytics Report', 14, 20);
      doc.setFontSize(12);
      doc.text(`Period: ${periodLabel}`, 14, 30);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 38);

      // Summary
      doc.setFontSize(14);
      doc.text('Summary', 14, 52);
      doc.setFontSize(10);
      const summaryData = [
        ['Total Cash Revenue', `$${metrics.totalCashRevenue.toLocaleString()}`],
        ['Total Cash Shares', metrics.totalCashShares.toLocaleString()],
        ['Bank Revenue', `$${metrics.bankRevenue.toLocaleString()}`],
        ['Crypto Revenue', `$${metrics.cryptoRevenue.toLocaleString()}`],
        ['Commission Shares', metrics.totalCommissionShares.toLocaleString()],
        ['Converted Shares', metrics.totalConvertedShares.toLocaleString()],
        ['Total Withdrawn', `$${metrics.totalWithdrawn.toLocaleString()}`],
        ['Net Cash Position', `$${metrics.netCashPosition.toLocaleString()}`],
      ];

      let y = 60;
      summaryData.forEach(([label, value]) => {
        doc.text(label, 14, y);
        doc.text(value, 100, y);
        y += 7;
      });

      // Data table
      doc.setFontSize(14);
      doc.text('Daily Data', 14, y + 10);
      doc.setFontSize(8);
      y += 20;
      
      data.slice(0, 20).forEach((d) => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${d.label}: Revenue $${d.revenue.toLocaleString()}, Shares ${d.shares.toLocaleString()}`, 14, y);
        y += 5;
      });

      doc.save(`sales-analytics-${periodLabel}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setExporting(null);
      setShowMenu(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm transition-colors"
      >
        📥 Export
        <span className="text-sm">▼</span>
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 w-40">
          <button onClick={exportCSV} disabled={exporting !== null}
            className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2 disabled:opacity-50">
            {exporting === 'csv' ? '⏳' : '📄'} CSV
          </button>
          <button onClick={exportExcel} disabled={exporting !== null}
            className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2 disabled:opacity-50">
            {exporting === 'excel' ? '⏳' : '📊'} Excel
          </button>
          <button onClick={exportPDF} disabled={exporting !== null}
            className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2 disabled:opacity-50">
            {exporting === 'pdf' ? '⏳' : '📕'} PDF
          </button>
        </div>
      )}
    </div>
  );
};

export default ExportOptions;

